import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Calculator, EyeOff, FileText, Send, Trophy } from 'lucide-react';
import { Badge, StatusBadge } from '@/components/common/Badge';
import { Button } from '@/components/common/Button';
import { Card } from '@/components/common/Card';
import { ConfirmDialog } from '@/components/common/Modal';
import { ErrorState, LoadingState } from '@/components/common/States';
import { PageHeader } from '@/components/layout/PageHeader';
import { DataTable, type Column } from '@/components/tables/DataTable';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { examinationsApi, resultsApi } from '@/services/api/endpoints/examinations';
import { queryKeys } from '@/services/api/queryKeys';
import { useAuth } from '@/features/auth/useAuth';
import { ROUTES } from '@/app/router/routes';
import { PERMISSIONS } from '@/types/permissions';
import type { StudentResult } from '@/types/examination';
import { formatDateTime, orDash } from '@/utils/format';
import { canCalculateResults } from './shared';

const PAGE_SIZE = 25;

type Action = 'calculate' | 'publish' | 'unpublish';

/** What each action does, said before it is done. */
const CONFIRMATIONS: Record<Action, { title: string; message: string; label: string; destructive?: boolean }> = {
  calculate: {
    title: 'Calculate results?',
    message:
      'Every result for this examination is worked out again from approved marks, and positions are ' +
      're-ranked across the whole class. Anything calculated before is replaced. Papers with marks ' +
      'still awaiting approval are refused by the server.',
    label: 'Calculate',
  },
  publish: {
    title: 'Publish results?',
    message:
      'Results are released to report cards, and students and their families are notified. ' +
      'Check the table before you publish.',
    label: 'Publish',
  },
  unpublish: {
    title: 'Withdraw published results?',
    message:
      'These results have already been released — families may have seen them and been notified. ' +
      'Withdrawing hides them again, but it cannot undo what was seen. The audit log records who ' +
      'withdrew them.',
    label: 'Withdraw results',
    destructive: true,
  },
};

/**
 * Sort keys the table offers. The values sorted are exactly the ones the
 * server returned — nothing is derived here, only ordered.
 */
const SORTERS: Record<string, (row: StudentResult) => string | number | undefined> = {
  position: (row) => row.position,
  studentName: (row) => row.studentName,
  totalObtained: (row) => row.totalObtained,
  percentage: (row) => row.percentage,
  grade: (row) => row.grade,
};

/**
 * Results for one examination: calculation, publication, and the table.
 *
 * Every figure here — totals, percentages, grades, positions — is the server's.
 * Results are stored when calculated rather than derived on every read, so this
 * screen only ever shows what a report card would say. The browser sorts and
 * pages the rows it was given; it never adds anything up.
 */
export function ResultsPage() {
  const { id: examinationId = '' } = useParams();
  const { can } = useAuth();
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const queryClient = useQueryClient();

  const [confirming, setConfirming] = useState<Action | null>(null);
  // The server returns results highest first; that is the default order.
  const [sort, setSort] = useState<string | undefined>(undefined);
  const [page, setPage] = useState(0);

  const examination = useQuery({
    queryKey: queryKeys.examinations.detail(examinationId),
    queryFn: () => examinationsApi.get(examinationId),
    enabled: examinationId !== '',
  });

  const results = useQuery({
    queryKey: queryKeys.examinations.results(examinationId),
    queryFn: () => resultsApi.list(examinationId),
    enabled: examinationId !== '',
  });

  const act = useMutation({
    mutationFn: async (action: Action): Promise<string> => {
      switch (action) {
        case 'calculate': {
          const { calculated } = await resultsApi.calculate(examinationId);
          return `Calculated ${calculated} results`;
        }
        case 'publish': {
          const { published } = await resultsApi.publish(examinationId);
          return `Published ${published} results`;
        }
        case 'unpublish': {
          const { withdrawn } = await resultsApi.unpublish(examinationId);
          return `Withdrew ${withdrawn} results`;
        }
      }
    },
    onSuccess: (message) => {
      toast.success(message);
      setConfirming(null);
      // The examination's status moves with these actions, and results with it.
      void queryClient.invalidateQueries({ queryKey: queryKeys.examinations.all });
    },
    onError: (error) => {
      setConfirming(null);
      handleApiError(error);
    },
  });

  const sorted = useMemo(() => {
    const rows = results.data ?? [];
    if (sort === undefined) {
      return rows;
    }
    const [property = '', direction] = sort.split(',');
    const read = SORTERS[property];
    if (read === undefined) {
      return rows;
    }
    const factor = direction === 'desc' ? -1 : 1;
    return [...rows].sort((a, b) => {
      const left = read(a);
      const right = read(b);
      // Missing values (no position, no grade) sink regardless of direction.
      if (left === undefined || left === null) {
        return right === undefined || right === null ? 0 : 1;
      }
      if (right === undefined || right === null) {
        return -1;
      }
      if (typeof left === 'number' && typeof right === 'number') {
        return (left - right) * factor;
      }
      return String(left).localeCompare(String(right)) * factor;
    });
  }, [results.data, sort]);

  if (examination.isError) {
    return <ErrorState error={examination.error} onRetry={() => void examination.refetch()} />;
  }
  if (examination.isPending) {
    return <LoadingState label="Loading the examination" />;
  }

  const exam = examination.data;
  const rows = results.data ?? [];
  const published = exam.status === 'PUBLISHED';
  const canCalculate = can(PERMISSIONS.RESULT_CALCULATE) && canCalculateResults(exam.status);
  // The server publishes only from RESULTS_PROCESSING (i.e. after a calculation).
  const canPublish = can(PERMISSIONS.RESULT_PUBLISH) && exam.status === 'RESULTS_PROCESSING' && rows.length > 0;
  const canUnpublish = can(PERMISSIONS.RESULT_PUBLISH) && published;
  const ranked = rows.some((row) => row.position !== undefined && row.position !== null);

  const pageCount = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount - 1);
  const visible = sorted.slice(currentPage * PAGE_SIZE, (currentPage + 1) * PAGE_SIZE);

  const onSort = (property: string) => {
    setPage(0);
    setSort((current) => {
      const [active, direction] = (current ?? '').split(',');
      if (active !== property) {
        // Figures read best highest-first; names and positions from the top.
        return property === 'studentName' || property === 'position' || property === 'grade'
          ? `${property},asc`
          : `${property},desc`;
      }
      return direction === 'asc' ? `${property},desc` : `${property},asc`;
    });
  };

  const columns: Column<StudentResult>[] = [
    ...(ranked
      ? [
          {
            key: 'position',
            header: 'Position',
            sortKey: 'position',
            render: (row: StudentResult) => (
              <span className="font-medium tabular-nums text-ink">{orDash(row.position)}</span>
            ),
          },
        ]
      : []),
    {
      key: 'student',
      header: 'Student',
      sortKey: 'studentName',
      render: (row) => (
        <>
          <span className="block text-ink">{orDash(row.studentName)}</span>
          <span className="block font-mono text-[11px] text-subtle">
            {orDash(row.studentNumber)}
            {row.rollNumber !== undefined && row.rollNumber !== null && ` · roll ${row.rollNumber}`}
          </span>
        </>
      ),
    },
    {
      key: 'section',
      header: 'Class',
      render: (row) => orDash(row.sectionName),
    },
    {
      key: 'total',
      header: 'Total',
      sortKey: 'totalObtained',
      className: 'whitespace-nowrap',
      render: (row) => (
        <span className="tabular-nums">
          <span className="text-ink">{row.totalObtained}</span> / {row.totalMaxMarks}
        </span>
      ),
    },
    {
      key: 'percentage',
      header: '%',
      sortKey: 'percentage',
      render: (row) => <span className="tabular-nums text-ink">{row.percentage}%</span>,
    },
    {
      key: 'grade',
      header: 'Grade',
      sortKey: 'grade',
      render: (row) => (
        <span className="flex items-center gap-1.5">
          <span className="font-medium text-ink">{orDash(row.grade)}</span>
          {row.gpa !== undefined && row.gpa !== null && (
            <span className="text-[11px] text-subtle tabular-nums">GPA {row.gpa}</span>
          )}
        </span>
      ),
    },
    {
      key: 'outcome',
      header: 'Outcome',
      render: (row) => (
        <span className="flex flex-col items-start gap-0.5">
          <Badge tone={row.passing ? 'success' : 'danger'}>{row.passing ? 'Pass' : 'Fail'}</Badge>
          <span className="text-[11px] text-subtle tabular-nums">
            {row.subjectsPassed} of {row.subjectsTotal} papers passed
          </span>
        </span>
      ),
    },
    {
      key: 'report',
      header: '',
      className: 'text-right',
      render: (row) => (
        <Link
          to={`${ROUTES.reportCard(exam.id, row.studentId)}?enrollment=${encodeURIComponent(row.enrollmentId)}`}
          className="inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-lg px-2.5 text-xs font-medium text-accent transition-colors hover:bg-surface-2"
        >
          <FileText className="h-3.5 w-3.5" aria-hidden="true" />
          Report card
        </Link>
      ),
    },
  ];

  const confirmation = confirming === null ? null : CONFIRMATIONS[confirming];

  return (
    <div>
      <Link
        to={ROUTES.examinationDetail(exam.id)}
        className="mb-3 inline-flex items-center gap-1.5 text-[13px] text-muted transition-colors hover:text-ink"
      >
        <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />
        {exam.name}
      </Link>

      <PageHeader
        title="Results"
        description={`${exam.name} · ${exam.gradeName}. Totals, percentages, grades and positions are calculated by the server from approved marks.`}
        icon={<Trophy className="h-5 w-5" aria-hidden="true" />}
        action={
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge status={exam.status} />
            {canCalculate && (
              <Button
                size="sm"
                variant={rows.length === 0 ? 'primary' : 'secondary'}
                disabled={act.isPending}
                onClick={() => setConfirming('calculate')}
              >
                <Calculator className="h-3.5 w-3.5" aria-hidden="true" />
                {rows.length === 0 ? 'Calculate results' : 'Recalculate'}
              </Button>
            )}
            {canPublish && (
              <Button size="sm" disabled={act.isPending} onClick={() => setConfirming('publish')}>
                <Send className="h-3.5 w-3.5" aria-hidden="true" />
                Publish
              </Button>
            )}
            {canUnpublish && (
              <Button
                size="sm"
                variant="secondary"
                disabled={act.isPending}
                onClick={() => setConfirming('unpublish')}
              >
                <EyeOff className="h-3.5 w-3.5" aria-hidden="true" />
                Withdraw
              </Button>
            )}
          </div>
        }
      />

      {published && exam.publishedAt !== undefined && (
        <p className="mb-4 rounded-lg bg-success-soft px-3 py-2 text-sm text-success">
          Published {formatDateTime(exam.publishedAt)}. Students and families can see these results.
        </p>
      )}
      {!published && rows.length > 0 && (
        <p className="mb-4 rounded-lg bg-info-soft px-3 py-2 text-sm text-info">
          Not yet published. Only staff can see these results.
        </p>
      )}

      <Card>
        <DataTable
          columns={columns}
          rows={results.isPending ? undefined : visible}
          rowKey={(row) => row.id}
          loading={results.isFetching}
          error={results.error}
          onRetry={() => void results.refetch()}
          sort={sort}
          onSort={onSort}
          emptyTitle="No results yet"
          emptyDescription={
            canCalculateResults(exam.status)
              ? 'Results appear once they are calculated from approved marks.'
              : 'Results can be calculated once the examination is completed and every marks sheet is approved.'
          }
          emptyIcon={<Trophy className="h-6 w-6" aria-hidden="true" />}
        />

        {sorted.length > PAGE_SIZE && (
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line px-4 py-3 text-xs text-muted">
            <span className="tabular-nums">
              {currentPage * PAGE_SIZE + 1}–{Math.min((currentPage + 1) * PAGE_SIZE, sorted.length)} of{' '}
              {sorted.length}
            </span>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="secondary"
                disabled={currentPage === 0}
                onClick={() => setPage(currentPage - 1)}
              >
                Previous
              </Button>
              <Button
                size="sm"
                variant="secondary"
                disabled={currentPage >= pageCount - 1}
                onClick={() => setPage(currentPage + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </Card>

      <ConfirmDialog
        open={confirmation !== null}
        title={confirmation?.title ?? ''}
        message={confirmation?.message ?? ''}
        confirmLabel={confirmation?.label ?? 'Confirm'}
        destructive={confirmation?.destructive ?? false}
        loading={act.isPending}
        onConfirm={() => confirming !== null && act.mutate(confirming)}
        onCancel={() => setConfirming(null)}
      />
    </div>
  );
}
